import type { RouteLocationNormalizedLoaded } from 'vue-router';

import { trace } from '@opentelemetry/api';
import * as Sentry from '@sentry/vue';
import { OpenApiError, type OpenApiClient, type OpenApiClientOptions } from '@zyno-io/openapi-client-codegen';
import { UserError, configureVfOpenApiClient } from '@zyno-io/vue-foundation';

import { emailApiFieldError } from '@/shared/helpers/email.helpers';

// Behavior to run when any configured client sees a 401. The full app registers this (see
// openapi-clients/shared.ts) to recover or clear the browser session, or eject the native session.
// Kept as a registration seam so this shared helper carries no static dependency on MobileBridge /
// @/global: lean surfaces like the embedded CX app configure their public client through here too,
// but register nothing — they have no session to tear down, and never pull those modules in.
let on401Handler: ((rejectedJwt: string | null) => void) | undefined;

export function register401Handler(handler: (rejectedJwt: string | null) => void) {
    on401Handler = handler;
}

// Returns the IDs the current screen was opened with (its route params and query values). Registered
// by each app entry against its own router, so this helper stays router-agnostic.
let currentRouteIdsProvider: (() => Iterable<string>) | undefined;

export function registerCurrentRouteIdsProvider(provider: () => Iterable<string>) {
    currentRouteIdsProvider = provider;
}

export function getRouteLocationIds(route: Pick<RouteLocationNormalizedLoaded, 'params' | 'query'>): string[] {
    return [...Object.values(route.params), ...Object.values(route.query)]
        .flat()
        .filter((value): value is string => typeof value === 'string' && value.length > 0);
}

// A 404 for a resource whose ID came from the URL (a stale link, a bookmark, a deleted record) is a
// user-facing not-found, not a code fault: the screen still shows its not-found UX, but Sentry
// shouldn't hear about it.
function isRouteDerived404(request: Request | undefined): boolean {
    if (!request || !currentRouteIdsProvider) return false;
    const routeIds = new Set(currentRouteIdsProvider());
    if (!routeIds.size) return false;
    return new URL(request.url).pathname.split('/').some(segment => {
        try {
            return routeIds.has(decodeURIComponent(segment));
        } catch {
            return routeIds.has(segment);
        }
    });
}

function getBearerToken(request: Request | undefined): string | null {
    const authorization = request?.headers.get('authorization');
    if (!authorization) return null;
    const match = /^Bearer\s+(.+)$/i.exec(authorization);
    return match?.[1] ?? null;
}

export function configureZynoOpenApiClient(name: string, client: OpenApiClient, options?: OpenApiClientOptions) {
    configureVfOpenApiClient(client, {
        ...options,

        onError(err, options) {
            const is401 = err instanceof OpenApiError && err.response?.status === 401;

            // A 401 is an expected auth/session condition, not a code fault. Hand it to the
            // registered handler and let the error drill down to the caller, but don't report it
            // to Sentry. Include the token from the actual request so a late response cannot
            // invalidate a newer JWT.
            if (is401) {
                on401Handler?.(getBearerToken(err.request));
                return;
            }

            // An email address the API refused (malformed, a domain that can't receive mail, or a DNS
            // lookup that failed) is the user's to fix or retry, not a fault. As a `UserError` every
            // `handleErrorAndAlert` shows its message as-is and keeps it out of Sentry; the rejection
            // rides along for a form that wants it on the field (the CX forms).
            const emailRejection = emailApiFieldError(err);
            if (emailRejection) return Object.assign(new UserError(emailRejection.message), { emailRejection });

            // Some endpoints 404 as an expected, caller-handled probe (e.g. Talk's GET /tenant for
            // tenants without Talk). When the request opts into it, let the error drill down to the
            // caller but don't report it to Sentry.
            const is404 = err instanceof OpenApiError && err.response?.status === 404;
            if (is404 && (options as { meta?: { ignoreSentry404?: boolean } }).meta?.ignoreSentry404) return;
            if (is404 && isRouteDerived404(err.request)) return;

            // Same idea, generalized: requests whose non-2xx statuses are expected, caller-handled
            // outcomes (e.g. validation 400s / conflict 409s surfaced inline in a form) can opt
            // those statuses out of Sentry while still throwing to the caller.
            const ignoredStatuses = (options as { meta?: { ignoreSentryStatuses?: number[] } }).meta?.ignoreSentryStatuses;
            if (err instanceof OpenApiError && err.response && ignoredStatuses?.includes(err.response.status)) return;

            Sentry.withScope(scope => {
                const spanCtx = trace.getActiveSpan()?.spanContext();
                if (spanCtx) {
                    scope.setContext('trace', {
                        trace_id: spanCtx.traceId,
                        span_id: spanCtx.spanId
                    });
                }
                if (err instanceof OpenApiError) {
                    scope.setFingerprint(['OpenApiError', String(err).replace(/^Error: /, '')]);
                } else if (err instanceof UserError) {
                    scope.setFingerprint(['UserError', String(err)]);
                }
                Sentry.captureException(err);
            });
        }
    });
}
